/**
 * The 5 steps of Add / Edit product. Every section stays mounted; a step only decides what is visible,
 * so uploads, refs and validation keep working across steps.
 */
export const PRODUCT_STEPS = [
  { id: "photos", label: "Photos", hint: "cover, angles, video" },
  { id: "basics", label: "Basics", hint: "name, colour, category" },
  { id: "details", label: "Details", hint: "saree, origin, marks" },
  { id: "making", label: "Making", hint: "who made it, loom photos" },
  { id: "review", label: "Review", hint: "check & save" },
] as const;

export type ProductStepId = (typeof PRODUCT_STEPS)[number]["id"];

export const isProductStepId = (value: unknown): value is ProductStepId =>
  PRODUCT_STEPS.some((step) => step.id === value);

export const stepIndex = (id: ProductStepId): number => PRODUCT_STEPS.findIndex((step) => step.id === id);

export const nextStep = (id: ProductStepId): ProductStepId | null => PRODUCT_STEPS[stepIndex(id) + 1]?.id ?? null;

export const previousStep = (id: ProductStepId): ProductStepId | null =>
  stepIndex(id) > 0 ? PRODUCT_STEPS[stepIndex(id) - 1].id : null;

/** `?step=details` → "details"; anything else → the fallback. */
export const stepFromSearch = (search: string | undefined, fallback: ProductStepId): ProductStepId => {
  const value = new URLSearchParams(search || "").get("step");
  return isProductStepId(value) ? value : fallback;
};

/** Which step holds the first field with a validation error, so Save can jump there. */
export const stepOfElement = (el: Element | null): ProductStepId | null => {
  const panel = el?.closest("[data-product-step]");
  const id = panel?.getAttribute("data-product-step");
  return isProductStepId(id) ? id : null;
};

const filled = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "object") {
    const row = value as { value?: unknown; _id?: unknown; id?: unknown };
    return filled(row.value) || filled(row._id) || filled(row.id);
  }
  return true;
};

export type ReviewItem = {
  id: string;
  label: string;
  detail: string;
  ok: boolean;
  /** Good to have, never blocks: shown with a dash and an Add button. */
  soft?: boolean;
  step: ProductStepId;
  /** Element to open and scroll to (a field inside a Making card), when the step alone isn't enough. */
  focusId?: string;
};

/** A required-to-publish custom field that is still empty; process fields carry an `hkp-tax-process-` focus id. */
export type MissingField = { name: string; focusId?: string };

const isProcessField = (field: MissingField) => String(field.focusId || "").startsWith("hkp-tax-process-");

type ReviewInput = {
  form: any;
  photos: Array<{ show?: unknown; pin?: unknown }>;
  weaveTypeApplicable: boolean;
  /** Custom fields the studio marked "needed to publish" that are still empty. */
  missingPublishFields: MissingField[];
  /** Photo views still missing ("blouse photo", "video"); good to have, never required. */
  missingViews?: string[];
};

const hasMedia = (slot: any): boolean =>
  Array.isArray(slot?.baseImages) && slot.baseImages.some((m: any) => m && !m._deleted);

/** "Left for when it's finished": what still needs filling before the piece can be published. Never blocks saving. */
export const buildReviewItems = ({ form, photos, weaveTypeApplicable, missingPublishFields, missingViews }: ReviewInput): ReviewItem[] => {
  const photoCount = (photos || []).length;
  const levels = ["artform", "type", "category", "subCategory", ...(weaveTypeApplicable ? ["weaveType"] : [])];
  const levelsFilled = levels.filter((key) => filled(form?.[key])).length;
  const slots: any[] = Array.isArray(form?.flexUploadSlots) ? form.flexUploadSlots : [];
  const slotsWithMedia = slots.filter(hasMedia).length;
  const pinned = (photos || []).some((p) => p?.pin === true || p?.pin === 1 || p?.pin === "1");
  const processMissing = (missingPublishFields || []).filter(isProcessField);
  const productMissing = (missingPublishFields || []).filter((f) => !isProcessField(f));
  return [
    {
      id: "photos",
      label: "Photos",
      detail: photoCount ? `${photoCount} · ${pinned ? "cover chosen" : "first photo is the cover"}` : "add at least one photo",
      ok: photoCount > 0,
      step: "photos",
    },
    ...(missingViews && missingViews.length
      ? [
          {
            id: "views",
            label: "Good to have",
            detail: `${missingViews.join(", ")} · not required`,
            ok: true,
            soft: true,
            step: "photos" as ProductStepId,
          },
        ]
      : []),
    {
      id: "name",
      label: "Name & colour",
      detail: filled(form?.productName) && filled(form?.variantCode) ? "done" : "both are needed",
      ok: filled(form?.productName) && filled(form?.variantCode),
      step: "basics",
    },
    {
      id: "category",
      label: "Category",
      detail: levelsFilled === levels.length ? `all ${levels.length} levels` : `${levelsFilled} of ${levels.length} levels`,
      ok: levelsFilled === levels.length,
      step: "basics",
    },
    {
      id: "making",
      label: "Making",
      detail: slots.length ? `${slotsWithMedia} of ${slots.length} steps have photos` : "no steps yet",
      ok: slots.length > 0 && slotsWithMedia > 0,
      step: "making",
    },
    ...(processMissing.length
      ? [
          {
            id: "making-fields",
            label: "Making details",
            detail: `${processMissing.map((f) => f.name).join(", ")} — fill before publishing`,
            ok: false,
            // These fields live only inside their Making card.
            step: "making" as ProductStepId,
            focusId: processMissing[0].focusId,
          },
        ]
      : []),
    {
      id: "attributes",
      label: "Your attributes",
      detail: productMissing.length ? `${productMissing.map((f) => f.name).join(", ")} — fill before publishing` : "done",
      ok: productMissing.length === 0,
      step: "details",
      focusId: productMissing[0]?.focusId,
    },
  ];
};

/** "4 of 6 filled" for a Details group. */
export const filledCount = (form: any, keys: string[]): { filled: number; total: number } => ({
  filled: keys.filter((key) => filled(form?.[key])).length,
  total: keys.length,
});

export const DETAIL_GROUP_KEYS = {
  material: ["zariUsed", "weightOfSaree", "weightOfZariUsed", "yarnType", "productionTimeInMonths", "workingHours"],
  origin: ["weavingCenter", "giId", "loomNum", "nameOfBrand", "nameOfWeaver"],
  marks: ["silkMark", "handloomMark", "designNum", "SrNo", "xuid"],
};

export const groupSummary = (form: any, keys: string[], what: string): string => {
  const { filled: n, total } = filledCount(form, keys);
  return `${n} of ${total} filled · ${what}`;
};
